import { useEffect, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { dateiTabelle, originalUrl, type TabellenBlatt } from '../../api/client';

/**
 * Wie die Vorschau ein Original zeigt (E30, erweitert 23.10.): PDF/Bild/umgewandeltes PDF im Rahmen, Excel (.xlsx) mit
 * GridCraft, .xls/.csv als eigene Tabelle mit markierter Fundzeile. Alles wird bei jedem Öffnen live vom Laufwerk
 * geholt und nicht gespeichert.
 */

export function Hinweis({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] leading-snug text-amber-900">
      <ExclamationTriangleIcon className="mt-px h-4 w-4 shrink-0" /><div>{children}</div>
    </div>
  );
}

function Laedt({ text }: { text: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-sm text-ps-dark-500">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-500" />
      {text}
    </div>
  );
}

async function holen(url: string): Promise<Response> {
  const r = await fetch(url, { credentials: 'include' });
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error((d as any).detail || `Fehler ${r.status}`); }
  return r;
}

/** PDF, Bild oder umgewandeltes PDF: als Blob geholt (Fehler lesbar statt JSON im Rahmen), PDF springt auf `seite`. */
export function BlobRahmen({ url, titel, seite, info }: { url: string; titel: string; seite: number | null; info?: string }) {
  const [blob, setBlob] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let weg = false;
    let objekt: string | null = null;
    setBlob(null); setFehler(null);
    holen(url).then((r) => r.blob())
      .then((b) => { if (weg) return; objekt = URL.createObjectURL(b); setBlob(objekt); })
      .catch((e: any) => { if (!weg) setFehler(e?.message ?? String(e)); });
    return () => { weg = true; if (objekt) URL.revokeObjectURL(objekt); };
  }, [url]);
  if (fehler) return <Hinweis>{fehler} Unter „Text“ steht der Inhalt aus dem Index.</Hinweis>;
  if (!blob) return <Laedt text={info ? 'Original wird geholt und umgewandelt …' : 'Original wird vom Laufwerk geholt …'} />;
  const ziel = blob + (seite ? `#page=${seite}` : '');
  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-1.5 text-[11.5px] text-ps-dark-500">
        <span>{info ?? ''}</span>
        <a href={ziel} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100">
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Neuer Tab
        </a>
      </div>
      <div className="relative min-h-[60dvh] flex-1 bg-gray-100 lg:min-h-0">
        <iframe key={ziel} src={ziel} title={titel} className="absolute inset-0 block h-full w-full border-0" />
      </div>
    </>
  );
}

/**
 * Excel mit GridCraft (Apache-2.0, selbst gehostet unter /gridcraft/). Abgeschottet: `sandbox` OHNE allow-same-origin
 * → eigener, undurchsichtiger Ursprung, kein Zugriff auf Cookies, Sitzung, API oder diese Seite. Die Datei geht per
 * Nachricht hinein (`gridcraft:open`); was GridCraft speichern will (`gridcraft:save`), wird verworfen — das Laufwerk
 * bleibt unangetastet.
 */
export function GridCraftAnsicht({ id, name, onAusweichen }: { id: string; name: string; onAusweichen: () => void }) {
  const rahmen = useRef<HTMLIFrameElement>(null);
  const [zustand, setZustandRoh] = useState<'start' | 'holt' | 'offen' | 'fehler'>('start');
  const zustandRef = useRef(zustand);
  const setZustand = (z: typeof zustand) => { zustandRef.current = z; setZustandRoh(z); };
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let weg = false;
    const bereitBis = setTimeout(() => {
      if (!weg && zustandRef.current === 'start') { setFehler('GridCraft hat nicht gestartet.'); setZustand('fehler'); }
    }, 30_000);
    const hoeren = async (e: MessageEvent) => {
      const fenster = rahmen.current?.contentWindow;
      if (!fenster || e.source !== fenster || typeof e.data !== 'object' || !e.data) return;
      if (e.data.type === 'gridcraft:ready') {
        setZustand('holt');
        try {
          const bytes = await (await holen(originalUrl(id))).arrayBuffer();
          if (weg) return;
          // Ziel „*": der Rahmen hat bewusst keinen eigenen Ursprung (sandbox); wir senden nur an genau dieses Fenster
          fenster.postMessage({ type: 'gridcraft:open', name, bytes }, '*', [bytes]);
          setZustand('offen');
        } catch (ex: any) { if (!weg) { setFehler(ex?.message ?? String(ex)); setZustand('fehler'); } }
      } else if (e.data.type === 'gridcraft:failed') {
        setFehler(`GridCraft konnte nicht starten (${String(e.data.error ?? 'unbekannt')}).`); setZustand('fehler');
      }
    };
    window.addEventListener('message', hoeren);
    return () => { weg = true; clearTimeout(bereitBis); window.removeEventListener('message', hoeren); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name]);

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-1.5 text-[11.5px] text-ps-dark-500">
        <span>Angezeigt mit GridCraft — Änderungen werden nicht gespeichert.</span>
        <button onClick={onAusweichen} className="flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100">
          <TableCellsIcon className="h-3.5 w-3.5" /> Einfache Tabelle
        </button>
      </div>
      {zustand === 'fehler' && <Hinweis>{fehler} <button onClick={onAusweichen} className="font-medium underline">Als einfache Tabelle anzeigen</button></Hinweis>}
      <div className="relative min-h-[60dvh] flex-1 bg-white lg:min-h-0">
        {zustand !== 'offen' && zustand !== 'fehler' && (
          <div className="absolute inset-0 z-10 flex bg-white"><Laedt text={zustand === 'start' ? 'GridCraft startet …' : 'Original wird vom Laufwerk geholt …'} /></div>
        )}
        <iframe ref={rahmen} src="/gridcraft/?host=parent" title={`${name} (GridCraft)`} sandbox="allow-scripts"
          className="absolute inset-0 block h-full w-full border-0" />
      </div>
    </>
  );
}

/** .xls/.csv (und .xlsx als Ausweiche): eigene Tabelle, Blätter als Reiter, Fundzeile markiert und im Blick. */
export function TabellenAnsicht({ id, blatt, zeile }: { id: string; blatt: string | null; zeile: number | null }) {
  const [blaetter, setBlaetter] = useState<TabellenBlatt[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [aktiv, setAktiv] = useState(0);
  const fundRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    let weg = false;
    setBlaetter(null); setFehler(null);
    dateiTabelle(id)
      .then((t) => {
        if (weg) return;
        setBlaetter(t.blaetter);
        const i = blatt ? t.blaetter.findIndex((b) => b.name === blatt) : 0;
        setAktiv(i >= 0 ? i : 0);
      })
      .catch((e: any) => { if (!weg) setFehler(e?.message ?? String(e)); });
    return () => { weg = true; };
  }, [id, blatt]);

  useEffect(() => { fundRef.current?.scrollIntoView({ block: 'center' }); }, [blaetter, aktiv]);

  if (fehler) return <Hinweis>{fehler} Unter „Text“ steht der Inhalt aus dem Index.</Hinweis>;
  if (!blaetter) return <Laedt text="Tabelle wird vom Laufwerk geholt …" />;
  const b = blaetter[aktiv];
  const fundBlatt = !blatt || b?.name === blatt;
  return (
    <div className="flex min-h-[60dvh] flex-1 flex-col lg:min-h-0">
      {blaetter.length > 1 && (
        <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-3 py-1.5">
          {blaetter.map((x, i) => (
            <button key={i} onClick={() => setAktiv(i)}
              className={`shrink-0 rounded-md px-2.5 py-1 text-[12px] ${i === aktiv ? 'bg-primary-500 text-white' : 'text-ps-dark-600 hover:bg-gray-100'}`}>
              {x.name ?? `Blatt ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      {b?.gekuerzt && <Hinweis>Angezeigt werden die ersten {b.zeilen.length.toLocaleString('de-CH')} von {b.gesamt.toLocaleString('de-CH')} Zeilen.</Hinweis>}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full border-collapse text-[12px]">
          <tbody>
            {b?.zeilen.map((r, i) => {
              const istFund = fundBlatt && zeile === i + 1;
              return (
                <tr key={i} ref={istFund ? fundRef : undefined} className={istFund ? 'bg-amber-100' : i === 0 ? 'bg-gray-50 font-semibold' : 'hover:bg-gray-50'}>
                  <td className="sticky left-0 border-b border-r border-gray-200 bg-gray-50 px-1.5 py-1 text-right text-[11px] text-ps-dark-400">{i + 1}</td>
                  {r.map((c, j) => <td key={j} className="max-w-[18rem] truncate border-b border-r border-gray-100 px-2 py-1 text-ps-dark-800" title={c}>{c}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
        {b && b.zeilen.length === 0 && <div className="p-4 text-sm text-ps-dark-500">Dieses Blatt ist leer.</div>}
      </div>
    </div>
  );
}
